<template>
  <section class="workout-history">
    <div class="workout-history__heading">
      <div>
        <h3>Trainingsplan-Historie</h3>
        <p>{{ rangeLabel }} · Erstellungen und gezählte Ergänzungen</p>
      </div>
      <v-btn icon small dark :loading="loading" aria-label="Trainingsplan-Historie aktualisieren" @click="$emit('refresh')">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </div>
    <v-alert v-if="error" dense outlined type="error">{{ error }}</v-alert>
    <template v-else>
      <div v-if="!loading" class="workout-history__summary">
        <span><strong>{{ createdCount }}</strong> neu erstellt</span>
        <span><strong>{{ editedRows.length }}</strong> bearbeitet</span>
        <span><strong>{{ singleExerciseEdits }}</strong> davon mit +1 Übung</span>
        <span><strong>Ø +{{ averageAdded }}</strong> Übungen je Bearbeitung</span>
      </div>
      <v-data-table
        dark
        dense
        :headers="headers"
        :items="historyRows"
        :loading="loading"
        :items-per-page="8"
        :mobile-breakpoint="0"
        :footer-props="{ 'items-per-page-options': [8, 16, 32] }"
        sort-by="date"
        :sort-desc="true"
        loading-text="Trainingsplan-Historie wird geladen …"
        no-data-text="Keine Trainingspläne in diesem Zeitraum."
        class="workout-history__table">
        <template v-slot:item.date="{ item }">
          <span class="workout-history__date">{{ formatDate(item.date) }}</span>
        </template>
        <template v-slot:item.customer="{ item }">
          {{ item.customer && item.customer.fullName ? item.customer.fullName : "Kunde nicht verfügbar" }}
        </template>
        <template v-slot:item.title="{ item }">
          <strong>{{ item.title || "Trainingsplan" }}</strong>
        </template>
        <template v-slot:item.kind="{ item }">
          <span :class="{ 'workout-history__edited': item.kind === 'edited' }">
            {{ item.kind === "edited" ? "Bearbeitet" : "Neu erstellt" }}
          </span>
        </template>
        <template v-slot:item.added="{ item }">
          <div v-if="item.kind === 'edited'" class="workout-history__change">
            <strong>+{{ item.added }} hinzugefügt · −{{ item.removed }} entfernt</strong>
            <small>{{ item.before }} → {{ item.after }} Übungen</small>
          </div>
          <span v-else>—</span>
        </template>
      </v-data-table>
    </template>
    <p class="workout-history__note">
      Pro bestehendem Split zählt eine Bearbeitung mit mindestens einer hinzugefügten Übung als ein Trainingsplan.
      Frühere Bearbeitungen ohne erfasste Änderungshistorie werden nicht rückwirkend ergänzt.
    </p>
  </section>
</template>

<script>
export default {
  name: "TrainerWorkoutHistory",
  props: {
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: String, default: "" },
    rangeLabel: { type: String, default: "" },
  },
  data() {
    return {
      headers: [
        { text: "Zeitpunkt", value: "date", width: 145 },
        { text: "Kunde", value: "customer", sortable: false },
        { text: "Split / Plan", value: "title" },
        { text: "Art", value: "kind", width: 115 },
        { text: "Änderungsumfang", value: "added", width: 245 },
      ],
    }
  },
  computed: {
    historyRows() {
      return this.rows.map((row) => ({
        ...row,
        kind: row.change?.kind || "created",
        added: Number(row.change?.added || 0),
        removed: Number(row.change?.removed || 0),
        before: Number(row.change?.before || 0),
        after: Number(row.change?.after || 0),
      }))
    },
    editedRows() {
      return this.historyRows.filter((row) => row.kind === "edited")
    },
    createdCount() {
      return this.historyRows.length - this.editedRows.length
    },
    singleExerciseEdits() {
      return this.editedRows.filter((row) => row.added === 1).length
    },
    averageAdded() {
      const total = this.editedRows.reduce((sum, row) => sum + row.added, 0)
      return new Intl.NumberFormat("de-CH", { maximumFractionDigits: 1 }).format(this.editedRows.length ? total / this.editedRows.length : 0)
    },
  },
  methods: {
    formatDate(value) {
      const date = this.$moment(value)
      return date.isValid() ? date.format("DD.MM.YYYY HH:mm") : "Datum fehlt"
    },
  },
}
</script>

<style scoped>
.workout-history {
  padding: 18px;
  border: 1px solid #334155;
  border-radius: 16px;
  color: #e2e8f0;
  background: #0f172a;
}
.workout-history__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.workout-history__heading h3 { margin: 0; font-size: 1.05rem; }
.workout-history__heading p,
.workout-history__note { color: #94a3b8; font-size: 0.78rem; line-height: 1.5; }
.workout-history__summary { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0 16px; }
.workout-history__summary span { padding: 6px 10px; border-radius: 8px; background: #1e293b; font-size: 0.75rem; }
.workout-history__summary strong { color: #f8fafc; }
.workout-history__table { background: transparent !important; }
.workout-history__date { white-space: nowrap; }
.workout-history__edited { color: #7dd3fc; }
.workout-history__change { padding: 8px 0; font-size: 0.75rem; }
.workout-history__change small { display: block; margin-top: 3px; color: #94a3b8; }
.workout-history__note { margin: 14px 0 0; }
</style>
